import { ElMessage } from 'element-plus'
import { getMyShareId, setMyShareId } from './auth'
import { generateShareId as apiGenerateShareId, bindRelation as apiBindRelation } from '@/api/auth'

/**
 * 通用分享工具 —— 任意页面一键生成带推荐人 share_id 的链接
 *
 * 推荐关系建立流程（全局一致）：
 *   1. 用户 A 登录后调用 ShareManager.init(userId) → 后端生成 share_${userId}_xxxxxxxx
 *   2. 用户 A 分享任意页面时调 ShareManager.copyShareUrl() / generateShareUrl('/some-path')
 *   3. 朋友 B 打开这个链接 ?share_id=xxx
 *      - 未登录：路由守卫存 localStorage → B 注册/登录时自动建立推荐关系
 *      - 已登录：路由守卫自动调 bindRelation 接口绑定
 */
export class ShareManager {

  /**
   * ✅ 登录后调用一次 —— 确保当前用户有自己的 shareId
   * 优先读本地缓存，没有再从后端拉（后端会保证幂等，已存在直接返回）
   *
   * @param {number|string} userId  当前登录用户的 ID
   */
  static async init (userId) {
    if (!userId) return
    const cached = getMyShareId()
    if (cached) return cached

    try {
      const res = await apiGenerateShareId(userId)
      // 兼容多种响应格式：{ data: 'share_xxx' } 或 { data: { data: 'share_xxx' } }
      const sid = res?.data ?? res?.data?.data ?? res
      if (typeof sid === 'string' && sid.startsWith('share_')) {
        setMyShareId(sid)
        console.log('✅ shareId 已缓存:', sid)
        return sid
      }
    } catch (e) {
      console.warn('initShareId 失败（不影响主流程）:', e.message)
    }
    return ''
  }

  /** 获取当前用户自己的 shareId（同步，拿不到返回空串） */
  static getMyShareId () {
    return getMyShareId()
  }

  /**
   * 生成「任意页面」的分享链接 —— 自动带上当前用户的 share_id
   *
   * @param {string} path   目标页面路径（如 /user/123、/shop、/card-detail/456）
   *                        不传则用当前页面
   * @returns {string} 完整可分享 URL
   */
  static generateShareUrl (path) {
    const base = window.location.origin
    const targetPath = path || window.location.pathname
    const sid = getMyShareId()

    const url = new URL(targetPath, base)
    if (sid) {
      url.searchParams.set('share_id', sid)
    }
    return url.toString()
  }

  /** 生成当前页面的分享链接 */
  static generateCurrentPageShareUrl () {
    return ShareManager.generateShareUrl(window.location.pathname + window.location.search)
  }

  /**
   * 一键复制分享链接到剪贴板
   * @param {string} [path] 目标路径，不传用当前页
   */
  static async copyShareUrl (path) {
    const url = ShareManager.generateShareUrl(path)
    try {
      await navigator.clipboard.writeText(url)
      ElMessage.success('分享链接已复制，快去发给好友吧~')
      return url
    } catch (e) {
      // 兜底
      const ta = document.createElement('textarea')
      ta.value = url
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch (_) {}
      document.body.removeChild(ta)
      ElMessage.success('分享链接已复制')
      return url
    }
  }

  /**
   * 🔗 已登录用户通过分享链接访问时，调用此接口自动绑定推荐关系
   * 内部会调用名片后端 /api/relation/bind
   *
   * @param {string}   shareId        访问链接里的 share_id
   * @param {number|string} currentUserId  当前登录用户 ID
   */
  static async bindForCurrentUser (shareId, currentUserId) {
    if (!shareId || !currentUserId) return { ok: false, reason: '参数缺失' }

    // 不能自己推荐自己
    try {
      const parts = shareId.split('_')
      if (parts.length >= 2 && String(parts[1]) === String(currentUserId)) {
        return { ok: true, skipped: true, reason: '自己推荐自己' }
      }
    } catch (_) {}

    try {
      const res = await apiBindRelation(shareId, currentUserId)
      const ok = res?.code === 200 || res?.code === 0 || res?.success === true
      console.log('🔗 推荐关系绑定:', ok ? '成功' : '跳过/失败', res)
      return { ok, raw: res }
    } catch (e) {
      console.warn('🔗 bindRelation 失败（用户可能已绑定过）:', e.message)
      return { ok: false, reason: e.message }
    }
  }

  /**
   * 从分享ID中解析推荐人 userId（格式 share_{userId}_xxxxxxxx）
   */
  static extractReferrerUserId (shareId) {
    if (!shareId) return null
    const parts = shareId.split('_')
    if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
      return Number(parts[1])
    }
    return null
  }
}

export default ShareManager
